/**
 * Shared TanStack React Query client.
 * ---------------------------------------------------------------------------
 * This is the single `QueryClient` instance the whole app shares (wired in
 * `App.tsx` via `<QueryClientProvider>`).
 *
 * The defaults are chosen to be NON-BREAKING: adopting React Query must not
 * change the UX of screens that already work. The legacy `useSupabaseCrud`
 * hooks fetch once (on an explicit trigger) and never silently refetch, so the
 * defaults here deliberately match that quiet behavior:
 *
 *   - refetchOnWindowFocus: false  — the app never re-queried on tab focus
 *     before, and turning it on would surprise users with reloads.
 *   - staleTime: 30s               — within 30s, navigating back to a screen
 *     serves cached data instantly (the whole point of adopting RQ) instead of
 *     re-hitting Supabase. After 30s the data is considered stale and the next
 *     mount refetches in the background.
 *   - gcTime: 5min                 — unused/inactive query caches are garbage-
 *     collected 5 minutes after their last observer unmounts.
 *   - retry: 1                     — one automatic retry on a failed query
 *     (Supabase REST blips), then surface the error. Mutations are not retried.
 *
 * Keep this list short and well-justified. If you change a default, explain
 * WHY here so the next person doesn't "fix" it back.
 */
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s
      gcTime: 5 * 60_000, // 5min
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
